/*
 * gmx-commission.js — the ARTWORK UPLOAD dropzone on the CustomMarx commission
 * page (sections/gmx-commission.liquid, styled by .gmx-cmn-dz* in
 * assets/gmx-custommarx.css). Ported from the `Dropzone` component in the
 * Ponies mockup (`public/custommarx-commission.jsx`).
 *
 * Two shapes, one UI. The section decides which by the markup it renders:
 *
 *   PRODUCT MODE (data-slots > 0)
 *     The section found a commission product and wrapped the page in
 *     {% form 'product' %}. Shopify uploads one file per line item property,
 *     so the section renders N single-file inputs named properties[Artwork 1..N].
 *     They are visually hidden; this script copies the chosen File objects into
 *     them with DataTransfer and enables only the ones that hold a file, so an
 *     unused slot never posts an empty property. Files past slot N stay in the
 *     preview strip and are labelled honestly as "email this one".
 *
 *   NOTE MODE (data-slots = 0)
 *     No commission product exists, so the page still posts through
 *     {% form 'contact' %}, which cannot carry files at all. The dropzone is
 *     preview-only and the section prints a note under it. The chosen file
 *     names are written into a hidden text field so the request email says
 *     which artwork to expect.
 *
 * Markup contract (data attributes, so class names stay free for styling):
 *   [data-gmx-dropzone]        root; data-max = UI file cap, data-slots = real inputs
 *   [data-gmx-dz-zone]         the <button> that opens the picker and takes drops
 *   [data-gmx-dz-picker]       hidden multiple <input type=file>, deliberately unnamed
 *   [data-gmx-dz-slot]         the named single-file inputs (product mode only)
 *   [data-gmx-dz-list]         <ul> preview strip
 *   [data-gmx-dz-msg]          inline status line (role="status")
 *   [data-gmx-dz-manifest]     hidden text input for the file names (note mode only)
 *
 * Accessibility: the zone is a real <button>, so Enter and Space open the
 * picker with no key handling of our own. Every state change is announced in
 * the status line. Motion is CSS-only and already respects
 * prefers-reduced-motion in gmx-custommarx.css.
 */
(function () {
  var IMAGE_RE = /^image\//;
  var PDF_RE = /^application\/pdf$/;
  var NAME_RE = /\.(png|jpe?g|jpg|gif|webp|avif|heic|heif|svg|pdf)$/i;

  /* Some browsers hand over an empty `type` for SVG and HEIC, so the file name
     is the second opinion rather than the only one. */
  function accepted(file) {
    return IMAGE_RE.test(file.type) || PDF_RE.test(file.type) || NAME_RE.test(file.name);
  }

  function previewable(file) {
    return IMAGE_RE.test(file.type) && file.type !== "image/heic";
  }

  /* Writing a FileList needs the DataTransfer constructor. It has been in every
     evergreen browser for years, but a failure here must not cost the shopper
     their previews — the dropzone falls back to note behaviour instead. */
  function canWriteFiles() {
    try {
      return new DataTransfer().files.length === 0;
    } catch (err) {
      return false;
    }
  }

  function Dropzone(root) {
    this.root = root;
    this.zone = root.querySelector("[data-gmx-dz-zone]");
    this.picker = root.querySelector("[data-gmx-dz-picker]");
    this.list = root.querySelector("[data-gmx-dz-list]");
    this.msg = root.querySelector("[data-gmx-dz-msg]");
    this.manifest = root.querySelector("[data-gmx-dz-manifest]");
    this.slots = Array.prototype.slice.call(root.querySelectorAll("[data-gmx-dz-slot]"));
    this.max = parseInt(root.getAttribute("data-max"), 10) || 8;
    this.items = [];
    this.depth = 0;

    if (this.slots.length && !canWriteFiles()) this.slots = [];

    this.bind();
  }

  Dropzone.prototype.bind = function () {
    var self = this;

    this.zone.addEventListener("click", function () {
      self.picker.click();
    });

    this.picker.addEventListener("change", function () {
      self.add(self.picker.files);
      /* The picker is a staging input only. Clearing it means picking the same
         file twice in a row still fires `change`. */
      self.picker.value = "";
    });

    /* dragleave fires whenever the pointer crosses onto a child of the zone, so
       the highlight is refcounted instead of toggled. */
    this.zone.addEventListener("dragenter", function (event) {
      event.preventDefault();
      self.depth += 1;
      self.zone.classList.add("is-over");
    });
    this.zone.addEventListener("dragover", function (event) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      self.zone.classList.add("is-over");
    });
    this.zone.addEventListener("dragleave", function () {
      self.depth -= 1;
      if (self.depth <= 0) {
        self.depth = 0;
        self.zone.classList.remove("is-over");
      }
    });
    this.zone.addEventListener("drop", function (event) {
      event.preventDefault();
      self.depth = 0;
      self.zone.classList.remove("is-over");
      if (event.dataTransfer) self.add(event.dataTransfer.files);
    });

    this.list.addEventListener("click", function (event) {
      var button = event.target.closest("[data-gmx-dz-remove]");
      if (!button) return;
      self.remove(parseInt(button.getAttribute("data-gmx-dz-remove"), 10));
    });
  };

  Dropzone.prototype.add = function (fileList) {
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;

    var rejected = [];
    var room = this.max - this.items.length;
    var overflow = 0;
    var self = this;

    files.forEach(function (file) {
      if (!accepted(file)) {
        rejected.push(file.name);
        return;
      }
      if (room <= 0) {
        overflow += 1;
        return;
      }
      room -= 1;
      self.items.push({
        file: file,
        name: file.name,
        url: previewable(file) ? URL.createObjectURL(file) : null,
      });
    });

    this.render();
    this.announce(rejected, overflow);
  };

  Dropzone.prototype.remove = function (index) {
    var item = this.items[index];
    if (!item) return;
    if (item.url) URL.revokeObjectURL(item.url);
    this.items.splice(index, 1);
    this.render();
    this.announce([], 0);
    this.zone.focus();
  };

  Dropzone.prototype.render = function () {
    var self = this;
    var slotCount = this.slots.length;

    this.list.textContent = "";
    this.items.forEach(function (item, index) {
      var extra = slotCount > 0 && index >= slotCount;
      var li = document.createElement("li");
      li.className = "gmx-cmn-dz__file" + (extra ? " gmx-cmn-dz__file--extra" : "");

      if (item.url) {
        var img = document.createElement("img");
        img.className = "gmx-cmn-dz__thumb";
        img.src = item.url;
        img.alt = item.name;
        li.appendChild(img);
      } else {
        var chip = document.createElement("span");
        chip.className = "gmx-cmn-dz__thumb gmx-cmn-dz__thumb--doc";
        chip.textContent = /\.pdf$/i.test(item.name) ? "PDF" : "FILE";
        li.appendChild(chip);
      }

      var name = document.createElement("span");
      name.className = "gmx-cmn-dz__name";
      name.textContent = item.name;
      li.appendChild(name);

      var button = document.createElement("button");
      button.type = "button";
      button.className = "gmx-cmn-dz__remove";
      button.setAttribute("data-gmx-dz-remove", String(index));
      button.setAttribute("aria-label", "Remove " + item.name);
      button.textContent = "✕";
      li.appendChild(button);

      self.list.appendChild(li);
    });

    this.list.hidden = this.items.length === 0;
    this.syncSlots();
    this.syncManifest();
  };

  /* One File per input, and an input with no file is disabled so Shopify never
     records an empty "Artwork 3" property on the order. */
  Dropzone.prototype.syncSlots = function () {
    var self = this;
    this.slots.forEach(function (input, index) {
      var item = self.items[index];
      var transfer = new DataTransfer();
      if (item) transfer.items.add(item.file);
      input.files = transfer.files;
      input.disabled = !item;
    });
  };

  Dropzone.prototype.syncManifest = function () {
    if (!this.manifest) return;
    this.manifest.value = this.items
      .map(function (item) {
        return item.name;
      })
      .join(", ");
  };

  Dropzone.prototype.announce = function (rejected, overflow) {
    var lines = [];
    var slotCount = this.slots.length;

    if (rejected.length) {
      lines.push(
        (rejected.length === 1 ? rejected[0] + " was skipped" : rejected.length + " files were skipped") +
          " — please send images or PDFs.",
      );
    }
    if (overflow) {
      lines.push(
        "You can add up to " +
          this.max +
          " files, so " +
          overflow +
          " more " +
          (overflow === 1 ? "was" : "were") +
          " skipped.",
      );
    }
    if (slotCount > 0 && this.items.length > slotCount) {
      lines.push(
        "The first " +
          slotCount +
          " files go with your order. Email the rest as a reply to your confirmation and we will add them.",
      );
    }

    this.msg.textContent = lines.join(" ");
    this.msg.hidden = lines.length === 0;
    this.msg.classList.toggle("gmx-cmn-dz__msg--warn", rejected.length > 0 || overflow > 0);
  };

  /* -------------------------------------------------------------- glitter pick */

  /* The commission page's product-form state reuses the PDP's glitter-color
     picker snippet (snippets/gmx-glitter-picker.liquid). The picker carries
     its own script (assets/gmx-glitter-picker.js) and self-initializes, so
     this page does not need to wire it up. */

  /* ------------------------------------------------------------ need-it-by date */

  /* The "Need it by?" field is a native <input type="date">. The page is
     full-page cached, so "today" must come from the browser at render time,
     not from Liquid — otherwise every visitor would see whichever day the
     cache last built. Setting `min` here also blocks past dates in browsers
     that honor it. */
  function todayISO() {
    var now = new Date();
    var month = String(now.getMonth() + 1).padStart(2, "0");
    var day = String(now.getDate()).padStart(2, "0");
    return now.getFullYear() + "-" + month + "-" + day;
  }

  /* Midnight-local Date for a yyyy-mm-dd string, built from parts so it never
     drifts a day from UTC parsing. */
  function parseISODate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
    var parts = value.split("-");
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }

  function toISODate(date) {
    var month = String(date.getMonth() + 1).padStart(2, "0");
    var day = String(date.getDate()).padStart(2, "0");
    return date.getFullYear() + "-" + month + "-" + day;
  }

  function sameDay(a, b) {
    return (
      a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    );
  }

  var WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  var MONTH_LABELS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  /* Custom desktop calendar popover, standing in for the native date picker so
     it can be themed. Mobile keeps the native `type="date"` sheet untouched —
     see isDesktopPointer() below. The posted value stays the same ISO
     yyyy-mm-dd string the native picker would have written, so nothing
     downstream (the line item property / contact field) changes shape. */
  function NeedByCalendar(input) {
    this.input = input;
    this.wrap = input.closest(".gmx-cmn-date-wrap") || input.parentElement;
    this.today = parseISODate(todayISO());
    this.selected = parseISODate(input.value);
    this.view = this.selected ? new Date(this.selected) : new Date(this.today);
    this.view.setDate(1);
    this.open = false;
    this.build();
    this.bind();
  }

  NeedByCalendar.prototype.build = function () {
    var pop = document.createElement("div");
    pop.className = "gmx-cmn-cal";
    pop.setAttribute("role", "dialog");
    pop.setAttribute("aria-label", "Choose a date");
    pop.hidden = true;

    var header = document.createElement("div");
    header.className = "gmx-cmn-cal__head";

    var prev = document.createElement("button");
    prev.type = "button";
    prev.className = "gmx-cmn-cal__nav";
    prev.setAttribute("aria-label", "Previous month");
    prev.textContent = "‹";

    var label = document.createElement("span");
    label.className = "gmx-cmn-cal__label";

    var next = document.createElement("button");
    next.type = "button";
    next.className = "gmx-cmn-cal__nav";
    next.setAttribute("aria-label", "Next month");
    next.textContent = "›";

    header.appendChild(prev);
    header.appendChild(label);
    header.appendChild(next);

    var weekdays = document.createElement("div");
    weekdays.className = "gmx-cmn-cal__weekdays";
    WEEKDAY_LABELS.forEach(function (day) {
      var cell = document.createElement("span");
      cell.textContent = day;
      weekdays.appendChild(cell);
    });

    var grid = document.createElement("div");
    grid.className = "gmx-cmn-cal__grid";

    pop.appendChild(header);
    pop.appendChild(weekdays);
    pop.appendChild(grid);

    this.wrap.appendChild(pop);
    this.pop = pop;
    this.prevBtn = prev;
    this.nextBtn = next;
    this.label = label;
    this.grid = grid;
  };

  NeedByCalendar.prototype.render = function () {
    var self = this;
    this.label.textContent = MONTH_LABELS[this.view.getMonth()] + " " + this.view.getFullYear();

    var atCurrentMonth =
      this.view.getFullYear() === this.today.getFullYear() && this.view.getMonth() === this.today.getMonth();
    this.prevBtn.disabled = atCurrentMonth;

    this.grid.textContent = "";

    var firstOfMonth = new Date(this.view.getFullYear(), this.view.getMonth(), 1);
    var startOffset = firstOfMonth.getDay();
    var daysInMonth = new Date(this.view.getFullYear(), this.view.getMonth() + 1, 0).getDate();

    for (var i = 0; i < startOffset; i++) {
      var filler = document.createElement("span");
      filler.className = "gmx-cmn-cal__day gmx-cmn-cal__day--empty";
      filler.setAttribute("aria-hidden", "true");
      this.grid.appendChild(filler);
    }

    var _loop = function (day) {
      var date = new Date(self.view.getFullYear(), self.view.getMonth(), day);
      var button = document.createElement("button");
      button.type = "button";
      button.className = "gmx-cmn-cal__day";
      button.textContent = String(day);

      var isPast = date < self.today;
      if (isPast) {
        button.disabled = true;
        button.className += " gmx-cmn-cal__day--past";
      } else {
        button.addEventListener("click", function () {
          self.choose(date);
        });
      }

      if (sameDay(date, self.today)) button.className += " gmx-cmn-cal__day--today";
      if (self.selected && sameDay(date, self.selected)) button.className += " gmx-cmn-cal__day--selected";

      self.grid.appendChild(button);
    };
    for (var day = 1; day <= daysInMonth; day++) _loop(day);
  };

  NeedByCalendar.prototype.choose = function (date) {
    this.selected = date;
    this.input.value = toISODate(date);
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
    this.close();
  };

  NeedByCalendar.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    this.view = this.selected ? new Date(this.selected) : new Date(this.today);
    this.view.setDate(1);
    this.render();
    this.pop.hidden = false;
  };

  NeedByCalendar.prototype.close = function () {
    if (!this.open) return;
    this.open = false;
    this.pop.hidden = true;
  };

  NeedByCalendar.prototype.toggle = function () {
    if (this.open) this.close();
    else this.show();
  };

  NeedByCalendar.prototype.bind = function () {
    var self = this;

    this.prevBtn.addEventListener("click", function () {
      self.view.setMonth(self.view.getMonth() - 1);
      self.render();
    });
    this.nextBtn.addEventListener("click", function () {
      self.view.setMonth(self.view.getMonth() + 1);
      self.render();
    });

    /* The input is made readonly on desktop, so pointerdown/click are the only
       way it would otherwise try to open the native picker. Both are
       intercepted so that popup and its keyboard entry never appear. */
    /* Toggle on click only — a real click fires pointerdown AND click, so
       toggling on both opened and instantly re-closed the popover. Pointerdown
       still gets preventDefault to keep focus/native picker away. */
    this.input.addEventListener("pointerdown", function (event) {
      event.preventDefault();
    });
    this.input.addEventListener("click", function (event) {
      event.preventDefault();
      self.toggle();
    });
    this.input.addEventListener("keydown", function (event) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        self.show();
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && self.open) self.close();
    });
    document.addEventListener(
      "pointerdown",
      function (event) {
        if (!self.open) return;
        if (self.wrap.contains(event.target)) return;
        self.close();
      },
      true,
    );
  };

  function isDesktopPointer() {
    return typeof window.matchMedia === "function" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  function initNeedByField(input) {
    if (input.dataset.gmxNeedByReady) return;
    input.dataset.gmxNeedByReady = "1";
    input.min = todayISO();

    if (isDesktopPointer()) {
      /* Desktop: the native calendar can't be themed, so it's replaced with
         gmx-cmn-cal below. `readonly` blocks typing and the native picker
         while still letting the input show its own formatted value. */
      input.readOnly = true;
      new NeedByCalendar(input);
      return;
    }

    /* Mobile/touch keeps the untouched native sheet. Clicking anywhere in the
       field opens it, not just the small calendar glyph. Not every browser
       implements showPicker(), and some throw if called outside a direct
       user gesture, so this is best-effort and never blocks typing/keyboard
       use. */
    input.addEventListener("click", function () {
      if (typeof input.showPicker !== "function") return;
      try {
        input.showPicker();
      } catch (err) {
        /* Ignored — the field still works as a normal date input. */
      }
    });
  }

  function init() {
    document.querySelectorAll("[data-gmx-dropzone]").forEach(function (root) {
      if (root.dataset.gmxDropzoneReady) return;
      root.dataset.gmxDropzoneReady = "1";
      new Dropzone(root);
    });

    document.querySelectorAll("[data-gmx-need-by]").forEach(initNeedByField);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  /* The theme editor re-renders a section in place. */
  document.addEventListener("shopify:section:load", init);
})();
